import {
  AcUnit,
  Cancel,
  CheckCircle,
  Delete,
  LocalCafe,
  LocalFireDepartment,
  ReportProblem,
  WarningAmber,
  WarningAmberOutlined,
} from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import DisplayDate from "components/DisplayDate";
import Loader from "components/Loader";
import axios, { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import { differenceInDays } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaDownload } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import SendWhatsAppDialog from "components/WhatsApp/SendWhatsAppDialog";
import { hasPermission } from "utils/permissionUtils";
import { getClientProfileCompleteness } from "../Admissions/AddAdmissions/clientProfileCompleteness";
import ClientAccounts from "./ClientAccounts";
import ClientApplications from "./ClientApplications";
import ClientAppointments from "./ClientAppointments";
import ClientEmails from "./ClientEmails";
import ClientInvoices from "./ClientInvoices";
import ClientNotes from "./ClientNotes";
import ClientReminders from "./ClientReminders";
import ClientDocumentsTab from "./documents/ClientDocumentsTab";
import ClientSourceFormDialog from "./ClientSourceFormDialog";
import DownloadClientData from "./DownloadClientData";
import ViewClientTasks from "./ViewClientTasks";
import { useClientSummary } from "./useClientSummary";

// Tab order must match <Tab> components below
const CLIENT_TABS = [
  "personal",
  "applications",
  "accounts",
  "notes",
  "tasks",
  "reminders",
  "appointments",
  "emails",
  "invoices",
  "documents",
];

/**
 * Kill switch for the whole client document collection surface. Set to false to hide the tab
 * again without touching permissions; the API has its own switch
 * (ClientDocumentAdminController.DOCUMENT_REQUESTS_ENABLED) and must agree.
 */
const DOCUMENTS_TAB_ENABLED = true;

const ViewClient = ({ color = "light" }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const client = state?.item;
  const [clientData, setClientData] = useState(null);
  const { id } = useParams();
  const contentRef = useRef(null);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [{ token, clientViewTabByClientId }, { refetchClient, setClientViewTab }] = useKothar();
  const canViewEmails = hasPermission(P.EMAIL.MESSAGE_READ);
  const canViewWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_READ);
  const canViewMessages = canViewEmails || canViewWhatsApp;
  const canViewInvoices = hasPermission(P.INVOICE.CLIENT_READ);
  const canViewDocuments = DOCUMENTS_TAB_ENABLED && hasPermission(P.CLIENT_DOCUMENT.READ);
  const canSendWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_SEND);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const actionsOpen = Boolean(actionsAnchor);
  const closeActions = () => setActionsAnchor(null);

  const tabs = useMemo(
    () =>
      CLIENT_TABS.filter((t) => t !== "emails" || canViewMessages)
        .filter((t) => t !== "invoices" || canViewInvoices)
        .filter((t) => t !== "documents" || canViewDocuments),
    [canViewMessages, canViewInvoices, canViewDocuments]
  );

  // Active tab: URL ?tab=notes → saved tab in context → legacy state.tab index → first tab
  const tabKeyFromUrl = searchParams.get("tab");
  const tabKeySaved = id ? clientViewTabByClientId[String(id)] : null;
  const tabKeyFromState =
    typeof state?.tab === "number" && state.tab >= 0 && state.tab < tabs.length
      ? tabs[state.tab]
      : typeof state?.tab === "string" && tabs.includes(state.tab)
      ? state.tab
      : null;
  const activeTabKey =
    (tabKeyFromUrl && tabs.includes(tabKeyFromUrl) && tabKeyFromUrl) ||
    (tabKeySaved && tabs.includes(tabKeySaved) && tabKeySaved) ||
    tabKeyFromState ||
    tabs[0];
  const selectedTab = tabs.indexOf(activeTabKey);

  const reactToPrintFn = useReactToPrint({
    contentRef,
  });

  // Keep URL in sync when we only have a saved tab (e.g. coming back to this client)
  useEffect(() => {
    if (!searchParams.get("tab") && activeTabKey) {
      setSearchParams({ tab: activeTabKey }, { replace: true });
    }
  }, [id, activeTabKey, searchParams, setSearchParams]);

  const handleDownload = (item) => {
    reactToPrintFn(item);
  };

  const { mutate: getClientData, isLoading } = useMutation({
    mutationFn: () => {
      return axios.get(`/clients/${id}`);
    },
    enabled: !!id,
    onSuccess: (response) => {
      setClientData(response?.data?.data);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { data: clientSummary } = useClientSummary(id);

  // The submission this client was converted from, if any. A directly added client has no
  // form behind it, so the button stays hidden instead of opening an empty dialog.
  const canViewSourceForm = hasPermission(P.FORMS.INTAKE_READ);
  const [sourceForm, setSourceForm] = useState(null);
  const [sourceFormLoading, setSourceFormLoading] = useState(false);
  const [sourceFormOpen, setSourceFormOpen] = useState(false);

  useEffect(() => {
    if (!id || !canViewSourceForm) {
      setSourceForm(null);
      return;
    }
    setSourceFormLoading(true);
    axios
      .get(`client-form/by-client/${id}`)
      .then((response) => setSourceForm(response?.data?.data ?? null))
      .catch(() => setSourceForm(null))
      .finally(() => setSourceFormLoading(false));
  }, [id, canViewSourceForm]);

  const tabCountBadge = (count) => {
    const n = Number(count);
    if (!Number.isFinite(n) || n <= 0) return null;
    return (
      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-blue-100 text-primary">
        {n}
      </span>
    );
  };

  const progressOptions = [
    {
      key: "LOST",
      label: "Lost",
      icon: ReportProblem,
      activeClass: "text-app-text bg-app-surface-muted border-app-border",
      inactiveClass: "text-slate-300 border-app-border hover:text-app-text-muted",
    },
    {
      key: "COLD",
      label: "Cold",
      icon: AcUnit,
      activeClass: "text-primary bg-blue-50 border-blue-200",
      inactiveClass: "text-slate-300 border-app-border hover:text-blue-500",
    },
    {
      key: "WARM",
      label: "Warm",
      icon: LocalCafe,
      activeClass: "text-amber-700 bg-amber-50 border-amber-200",
      inactiveClass: "text-slate-300 border-app-border hover:text-amber-500",
    },
    {
      key: "HOT",
      label: "Hot",
      icon: LocalFireDepartment,
      activeClass: "text-red-700 bg-red-50 border-red-200",
      inactiveClass: "text-slate-300 border-app-border hover:text-red-500",
    },
  ];

  const progressInfo = {
    HOT: "High intent and active lead. Ready for immediate action and close follow-up.",
    WARM: "Interested lead. Needs nurturing and regular engagement.",
    COLD: "Low engagement lead. Follow up occasionally to re-activate interest.",
    LOST: "Lead is no longer active or not likely to convert.",
  };

  const { mutate: updateClientProgress, isLoading: isProgressUpdating } = useMutation({
    mutationFn: async ({ progressStage }) => {
      // Whole-client PUT from a copy that may predate an admission or insurance entry. Leaving
      // isConverted out keeps the server's own flag instead of writing a stale one back.
      const { isConverted, ...rest } = clientData ?? {};
      return axios.put(`${API_URL}/clients/${id}`, {
        ...rest,
        progressStage,
      });
    },
    onSuccess: (_, vars) => {
      setClientData((prev) => ({ ...prev, progressStage: vars.progressStage }));
      toast.success(`Progress updated to ${vars.progressStage}`);
    },
    onError: () => {
      toast.error("Could not update progress");
    },
  });

  const handleProgressUpdate = (progressStage) => {
    if (!clientData || isProgressUpdating || clientData?.progressStage === progressStage) return;
    setClientData((prev) => ({ ...prev, progressStage }));
    updateClientProgress({ progressStage });
  };
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      deleteData(
        `${API_URL}/clients/${id}`,
        () => {
          toast.success("Client deleted successfully");
          refetchClient();
          navigate("/admin/client");
        },
        token
      );
    }
  };

  useEffect(() => {
    getClientData();
  }, []);

  const handleTabChange = (event, newIndex) => {
    const tabKey = tabs[newIndex] ?? tabs[0];
    setSearchParams({ tab: tabKey }, { replace: true });
    if (id) setClientViewTab(id, tabKey);
  };

  const daysUntilExpiry = differenceInDays(new Date(client?.visaExpiry), new Date());

  const isExpiringSoon = daysUntilExpiry < 90;

  const InfoSection = ({ title, children }) => (
    <div className="h-full">
      <Paper className="rounded-xl border border-app-border shadow-sm hover:shadow-md transition-all duration-300">
        <CardContent className="p-0">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-app-border bg-app-surface-muted/50">
            <Typography
              variant="h6"
              className="font-semibold flex items-center gap-2 text-base sm:text-lg"
            >
              <span className="w-1.5 h-5 sm:h-6 bg-blue-500 rounded-full shrink-0" />
              {title}
            </Typography>
          </div>
          <div className="p-4 sm:p-6">{children}</div>
        </CardContent>
      </Paper>
    </div>
  );

  const goEditClient = () => {
    navigate(`/admin/client/edit/${id}`, { state: { item: clientData } });
  };

  const hasClient = Boolean(clientData?.id);

  const { isComplete, missing, missingBySection } = useMemo(
    () => getClientProfileCompleteness(hasClient ? clientData : null),
    [clientData, hasClient]
  );

  if (!isLoading && !clientData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Typography>No client data available</Typography>
      </div>
    );
  }

  return (
    <>
      <div className="w-full mb-12 px-2 sm:px-4 dashBody mt-2 sm:mt-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl border border-app-border h-full">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen">
              <Loader />
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex-shrink-0 rounded-t-xl mb-0 px-3 sm:px-6 lg:px-8 py-3 sm:py-4 border-b border-app-border bg-app-surface">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                    <IconButton
                      size="small"
                      onClick={() => navigate(-1)}
                      className="text-app-text-secondary hover:bg-app-surface-muted shrink-0"
                      aria-label="Go back"
                    >
                      <IoArrowBack className="text-xl" />
                    </IconButton>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base sm:text-lg text-app-text truncate">
                        {clientData?.name}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {clientData?.isConverted ? (
                          <span className="inline-flex items-center gap-1 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle className="text-green-500" sx={{ fontSize: 16 }} />
                            <span className="text-xs font-medium text-green-600">Converted</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-red-50 px-2 py-0.5 rounded-full">
                            <Cancel className="text-red-500" sx={{ fontSize: 16 }} />
                            <span className="text-xs font-medium text-red-600">Not Converted</span>
                          </span>
                        )}
                        {sourceForm ? (
                          <button
                            type="button"
                            onClick={() => setSourceFormOpen(true)}
                            className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-primary hover:bg-blue-100"
                          >
                            View{" "}
                            {sourceForm.type === "client-details" ? "client form" : "check-in form"}
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Compact actions on small screens */}
                    <div className="flex items-center gap-1 shrink-0 sm:hidden">
                      <IconButton
                        size="small"
                        onClick={goEditClient}
                        aria-label="Edit client"
                        className="border border-app-border"
                      >
                        <FiEdit2 className="text-base" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => setActionsAnchor(e.currentTarget)}
                        aria-label="More client actions"
                      >
                        <MoreVertIcon className="text-app-text-secondary" />
                      </IconButton>
                      <Popover
                        open={actionsOpen}
                        anchorEl={actionsAnchor}
                        onClose={closeActions}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        marginThreshold={12}
                      >
                        <div className="p-1.5 min-w-[200px]">
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 p-2.5 hover:bg-app-surface-hover rounded text-left text-sm"
                            onClick={() => {
                              handleDownload(clientData);
                              closeActions();
                            }}
                          >
                            <FaDownload className="text-primary" />
                            Download Information
                          </button>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 p-2.5 hover:bg-app-surface-hover rounded text-left text-sm"
                            onClick={() => {
                              goEditClient();
                              closeActions();
                            }}
                          >
                            <FiEdit2 className="text-primary" />
                            Edit Client
                          </button>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 p-2.5 hover:bg-app-surface-hover rounded text-left text-sm text-red-600"
                            onClick={() => {
                              closeActions();
                              handleDelete(id);
                            }}
                          >
                            <Delete fontSize="small" className="text-red-600" />
                            Delete Client
                          </button>
                          {canSendWhatsApp ? (
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 p-2.5 hover:bg-app-surface-hover rounded text-left text-sm"
                              onClick={() => {
                                closeActions();
                                setWhatsAppOpen(true);
                              }}
                            >
                              WhatsApp
                            </button>
                          ) : null}
                        </div>
                      </Popover>
                    </div>
                  </div>

                  {/* Full actions from sm up */}
                  <div className="hidden sm:flex items-center gap-2 flex-wrap justify-end">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FaDownload />}
                      onClick={() => handleDownload(clientData)}
                    >
                      Download Information
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FiEdit2 />}
                      onClick={goEditClient}
                    >
                      Edit Client
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(id)}
                    >
                      Delete Client
                    </Button>
                    {canSendWhatsApp ? (
                      <Button variant="outlined" size="small" onClick={() => setWhatsAppOpen(true)}>
                        WhatsApp
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="flex flex-1 min-h-0 h-full">
                <div className="w-full p-1 sm:p-2 h-full flex flex-col min-w-0">
                  <div className="flex-shrink-0">
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        className="font-semibold"
                        sx={{
                          minHeight: 44,
                          "& .MuiTab-root": {
                            minHeight: 44,
                            minWidth: "auto",
                            px: { xs: 1.25, sm: 2 },
                            fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "1rem" },
                            fontWeight: 600,
                            textTransform: "none",
                          },
                        }}
                      >
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Personal Info{" "}
                              {!isComplete && (
                                <WarningAmberOutlined
                                  sx={{ fontSize: 16 }}
                                  className="text-amber-500"
                                />
                              )}
                            </span>
                          }
                        />
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Applications
                              {tabCountBadge(clientSummary?.applications)}
                            </span>
                          }
                        />
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Accounts
                              {tabCountBadge(clientSummary?.accounts)}
                              {clientSummary?.effectiveDueAmount > 0 && (
                                <Tooltip
                                  arrow
                                  title={
                                    <div>
                                      <div>
                                        Effective due: $
                                        {Number(clientSummary.effectiveDueAmount).toLocaleString(
                                          "en-AU",
                                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        )}
                                      </div>
                                      <div style={{ opacity: 0.8, marginTop: 2 }}>
                                        Total due: $
                                        {Number(clientSummary.totalDueAmount).toLocaleString(
                                          "en-AU",
                                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                        )}
                                      </div>
                                    </div>
                                  }
                                >
                                  <WarningAmber sx={{ fontSize: 16 }} className="text-amber-500" />
                                </Tooltip>
                              )}
                            </span>
                          }
                        />
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Notes
                              {tabCountBadge(clientSummary?.notes)}
                            </span>
                          }
                        />
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Tasks
                              {tabCountBadge(clientSummary?.tasks)}
                            </span>
                          }
                        />
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Reminders
                              {tabCountBadge(clientSummary?.reminders)}
                            </span>
                          }
                        />
                        <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Appointments
                              {tabCountBadge(clientSummary?.appointments)}
                            </span>
                          }
                        />
                        {canViewMessages && (
                          <Tab
                            label={
                              <span className="flex items-center gap-1.5">
                                Messages
                                {tabCountBadge(clientSummary?.emails)}
                              </span>
                            }
                          />
                        )}
                        {canViewInvoices && (
                          <Tab
                            label={
                              <span className="flex items-center gap-1.5">
                                Invoices
                                {tabCountBadge(clientSummary?.invoices)}
                                {clientSummary?.outstandingInvoices > 0 && (
                                  <Tooltip
                                    arrow
                                    title={
                                      <div>
                                        <div>
                                          {clientSummary.outstandingInvoices} unpaid invoice
                                          {clientSummary.outstandingInvoices > 1 ? "s" : ""}
                                        </div>
                                        <div style={{ opacity: 0.8, marginTop: 2 }}>
                                          Outstanding: $
                                          {Number(clientSummary.invoiceDueAmount).toLocaleString(
                                            "en-AU",
                                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                          )}
                                        </div>
                                      </div>
                                    }
                                  >
                                    <WarningAmber
                                      sx={{ fontSize: 16 }}
                                      className="text-amber-500"
                                    />
                                  </Tooltip>
                                )}
                              </span>
                            }
                          />
                        )}
                        {canViewDocuments && <Tab label="Documents" />}
                        {/* <Tab
                          label={
                            <span className="flex items-center gap-1.5">
                              Intake Forms
                              {tabCountBadge(clientSummary?.intakeForms)}
                            </span>
                          }
                        /> */}
                      </Tabs>
                    </Box>
                  </div>

                  <div className="mt-3 sm:mt-4 flex-1 overflow-y-auto overflow-x-hidden px-1 sm:px-0">
                    {selectedTab === 0 && (
                      <>
                        {!isComplete && (
                          <div className="flex flex-col sm:flex-row sm:items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4">
                            <WarningAmberOutlined
                              sx={{ fontSize: 18 }}
                              className="text-amber-500 shrink-0 mt-0.5"
                            />
                            <div className="min-w-0 text-sm text-amber-800">
                              <p className="mb-2">
                                {missing.length} field{missing.length > 1 ? "s" : ""} missing from
                                client profile. Please complete the profile.
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {missing.map((field) => (
                                  <Chip
                                    key={field.key}
                                    size="small"
                                    label={field.label}
                                    color="warning"
                                    variant="outlined"
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="columns-1 md:columns-2 xl:columns-3 gap-3 sm:gap-4 [column-fill:_balance]">
                          <Paper
                            elevation={0}
                            className="mb-3 sm:mb-4 inline-block w-full break-inside-avoid rounded-xl border border-app-border shadow-sm"
                          >
                            <CardContent className="space-y-4 !p-4 sm:!p-6">
                              <div className="flex items-start justify-center gap-4">
                                <div className="min-w-0 text-center w-full">
                                  <h2 className="text-lg sm:text-xl font-bold text-app-text truncate">
                                    {clientData?.name || "Client Name"}
                                  </h2>
                                  <p className="text-sm text-app-text truncate mt-1 break-all">
                                    {clientData?.email || "Not provided"}
                                  </p>
                                  <p className="text-sm text-app-text truncate">
                                    {clientData?.number || "Not provided"}
                                  </p>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border mt-1 ${
                                      clientData?.isConverted
                                        ? "bg-green-50 text-green-700 border-green-200"
                                        : "bg-red-50 text-red-700 border-red-200"
                                    }`}
                                  >
                                    {clientData?.isConverted ? "Converted" : "Not Converted"}
                                  </span>
                                </div>
                              </div>
                              <div className="pt-2 border-t border-app-border">
                                <div className="flex items-center justify-between mb-2">
                                  {isProgressUpdating && (
                                    <span className="text-[11px] text-app-text-muted">
                                      Updating...
                                    </span>
                                  )}
                                </div>
                                <div className="mx-auto grid grid-cols-4 gap-1.5 sm:gap-2">
                                  {progressOptions.map((option) => {
                                    const Icon = option.icon;
                                    const isActive = clientData?.progressStage === option.key;

                                    return (
                                      <Tooltip
                                        key={option.key}
                                        title={progressInfo[option.key] || ""}
                                        arrow
                                        placement="top"
                                      >
                                        <button
                                          type="button"
                                          onClick={() => handleProgressUpdate(option.key)}
                                          className={`rounded-lg border px-1.5 sm:px-2 py-2 text-center transition-all min-h-[44px] ${
                                            isActive ? option.activeClass : option.inactiveClass
                                          }`}
                                        >
                                          <Icon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                          <p className="text-[10px] sm:text-[11px] font-semibold mt-1">
                                            {option.label}
                                          </p>
                                        </button>
                                      </Tooltip>
                                    );
                                  })}
                                </div>
                              </div>
                            </CardContent>
                          </Paper>

                          <div className="mb-3 sm:mb-4 inline-block w-full break-inside-avoid">
                            <InfoSection title="Personal Information">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                <div>
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Gender
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.gender || "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Date of Birth
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    <DisplayDate value={clientData?.dob} fallback="Not provided" />
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Marital Status
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.maritalStatus || "Not provided"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    USI Number
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.usiNumber || "Not provided"}
                                  </p>
                                </div>
                                <div className="md:col-span-2">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Full Address
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.address || "Not provided"}
                                  </p>
                                </div>
                              </div>
                            </InfoSection>
                          </div>

                          <div className="mb-3 sm:mb-4 inline-block w-full break-inside-avoid">
                            <InfoSection title="Passport & Visa Information">
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="border-b border-app-border pb-3">
                                    <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                      Passport Number
                                    </p>
                                    <p className="text-sm font-semibold text-app-text">
                                      {clientData?.passportNumber || "Not provided"}
                                    </p>
                                  </div>
                                  <div className="border-b border-app-border pb-3">
                                    <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                      Issue Date
                                    </p>
                                    <p className="text-sm font-semibold text-app-text">
                                      <DisplayDate
                                        value={clientData?.dateOfIssue}
                                        fallback="Not provided"
                                      />
                                    </p>
                                  </div>
                                  <div className="border-b border-app-border pb-3">
                                    <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                      Passport Expiry Date
                                    </p>
                                    <p className="text-sm font-semibold text-app-text">
                                      <DisplayDate
                                        value={clientData?.passportExpiry}
                                        fallback="Not provided"
                                      />
                                    </p>
                                  </div>
                                  <div className="border-b border-app-border pb-3">
                                    <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                      Visa Type
                                    </p>
                                    <p className="text-sm font-semibold text-app-text">
                                      {clientData?.visaStatus || "Not provided"}
                                    </p>
                                  </div>
                                  <div className="border-b border-app-border pb-3">
                                    <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-2">
                                      Visa Lodged
                                    </p>
                                    <p className="text-sm font-semibold text-app-text mb-2">
                                      {clientData?.visaLodged ? "Yes" : "No"}
                                    </p>
                                  </div>
                                </div>
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-2">
                                    Visa Expiry Date
                                  </p>
                                  <p className="text-sm font-semibold text-app-text mb-2">
                                    <DisplayDate
                                      value={clientData?.visaExpiry}
                                      fallback="Not provided"
                                    />
                                  </p>
                                  {clientData?.visaExpiry && isExpiringSoon && (
                                    <div
                                      className={`inline-block px-3 py-2 rounded-lg text-xs font-medium ${
                                        daysUntilExpiry > 0
                                          ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                          : "bg-red-100 text-red-800 border border-red-200"
                                      }`}
                                    >
                                      {daysUntilExpiry > 0
                                        ? `⚠️ Visa expiring in ${daysUntilExpiry} days`
                                        : "🚨 Visa expired, please contact Client"}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </InfoSection>
                          </div>

                          <div className="mb-3 sm:mb-4 inline-block w-full break-inside-avoid">
                            <InfoSection title="Assignment & Referral Information">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Case Officer
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.caseOfficer || "Not provided"}
                                  </p>
                                </div>
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Referred By
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.referral || "-"}
                                  </p>
                                </div>
                              </div>
                            </InfoSection>
                          </div>
                          <div className="mb-3 sm:mb-4 inline-block w-full break-inside-avoid">
                            <InfoSection title="Emergency Contact">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Contact Name
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.emergencyContactName || "Not provided"}
                                  </p>
                                </div>
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Relation
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.emergencyRelation || "Not provided"}
                                  </p>
                                </div>
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Contact Number
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.emergencyContact || "Not provided"}
                                  </p>
                                </div>
                                <div className="border-b border-app-border pb-3">
                                  <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wide mb-1">
                                    Email
                                  </p>
                                  <p className="text-sm font-semibold text-app-text">
                                    {clientData?.emergencyEmail || "Not provided"}
                                  </p>
                                </div>
                              </div>
                            </InfoSection>
                          </div>
                        </div>
                      </>
                    )}
                    {selectedTab === 1 && (
                      <ClientApplications columns={["Date", "Note", "Created By"]} />
                    )}
                    {selectedTab === 2 && <ClientAccounts {...{ clientData }} />}
                    {selectedTab === 3 && <ClientNotes />}
                    {selectedTab === 4 && (
                      <ViewClientTasks columns={["Date", "Description", "Amount", "Status"]} />
                    )}
                    {selectedTab === 5 && <ClientReminders />}
                    {selectedTab === 6 && <ClientAppointments />}
                    {canViewMessages && selectedTab === 7 && <ClientEmails clientId={id} />}
                    {canViewInvoices && activeTabKey === "invoices" && (
                      <ClientInvoices clientId={id} />
                    )}
                    {canViewDocuments && activeTabKey === "documents" && (
                      <ClientDocumentsTab
                        clientId={id}
                        initialRequestId={state?.documentRequestId}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Hidden component for printing/downloading - only rendered when needed */}
      <div style={{ display: "none" }}>
        {contentRef && <DownloadClientData printData={clientData} contentRef={contentRef} />}
      </div>
      <ClientSourceFormDialog
        form={sourceForm}
        isLoading={sourceFormLoading}
        open={sourceFormOpen}
        onClose={() => setSourceFormOpen(false)}
      />
      <SendWhatsAppDialog
        open={whatsAppOpen}
        clientId={id}
        clientName={clientData?.name}
        onClose={() => setWhatsAppOpen(false)}
      />
    </>
  );
};

export default ViewClient;
